import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sparkles,
  Loader2,
  Crown,
  Key,
  CreditCard,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// Real Pro-only capabilities — every bullet maps to an actual unlock,
// grouped so the modal scans well. Keep titles short for ≤480px viewports.
const PRO_FEATURE_GROUPS = [
  {
    title: 'Compliance & Documents',
    items: [
      'E-Invoicing — IRN & signed QR via NIC GSP',
      'E-Way Bill generation, print & cancel',
      'GST Compliant Reports (GSTR-1 / 2 / 3B)',
      'Entity Ledger statements (Customer & Vendor)',
      'Delivery Challan workflow with PDF',
    ],
  },
  {
    title: 'Hardware, Templates & Branding',
    items: [
      'Thermal / 80mm POS Printer support',
      'Multi-Template PDFs (Modern, Classic, Thermal)',
      'Custom Brand Theme — logo, colour palette, fonts',
      'Per-document discount engine + inclusive-tax pricing',
    ],
  },
  {
    title: 'Multi-Tenant Power',
    items: [
      'Branch / Warehouse addresses on every document',
      'Multi-Currency invoicing (40+ currencies)',
      'Inventory adjustments with audit trail',
      'Sales Dashboard 3-dot actions (DC / EWB / Share)',
    ],
  },
  {
    title: 'Team & Support',
    items: [
      'Up to 10 user seats (1 Owner + 9 Staff)',
      'Role-based access (Owner, Manager, Staff, Viewer)',
      'Priority email + WhatsApp support',
      'One-time purchase — no recurring fee',
    ],
  },
];

export function UpgradeProModal({ open, onOpenChange, onUpgraded, user }) {
  const [licenseCode, setLicenseCode] = useState('');
  const [submittingCode, setSubmittingCode] = useState(false);
  const [rzpInfo, setRzpInfo] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get('/organization/upgrade/razorpay/key')
      .then((r) => setRzpInfo(r.data))
      .catch(() => setRzpInfo({ enabled: false }));
  }, [open]);

  const handleCodeUpgrade = async (e) => {
    e.preventDefault();
    if (!licenseCode.trim()) {
      toast.error('Enter a Pro license code');
      return;
    }
    setSubmittingCode(true);
    try {
      const r = await api.post('/organization/upgrade/with-code', {
        license_code: licenseCode.trim(),
      });
      toast.success(r.data.message || 'Upgraded to Pro!');
      onUpgraded?.(r.data.organization);
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Upgrade failed');
    } finally {
      setSubmittingCode(false);
    }
  };

  const handleRazorpayPay = async () => {
    if (!rzpInfo?.enabled) {
      toast.error('Razorpay payments are not configured. Please contact support.');
      return;
    }
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error('Could not load Razorpay. Check your network.');
      return;
    }
    setPaying(true);
    let order;
    try {
      const r = await api.post('/organization/upgrade/razorpay/order');
      order = r.data;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start payment');
      setPaying(false);
      return;
    }

    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount_paise,
      currency: order.currency,
      name: 'RupiWise',
      description: 'Pro License Upgrade',
      order_id: order.order_id,
      prefill: {
        email: order.email,
        name: order.name,
      },
      theme: { color: '#0d9488' },
      handler: async (resp) => {
        try {
          const verify = await api.post('/organization/upgrade/razorpay/verify', {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success(verify.data.message || 'Upgraded to Pro!');
          onUpgraded?.(verify.data.organization);
          onOpenChange(false);
        } catch (err) {
          const d = err?.response?.data?.detail;
          toast.error(typeof d === 'string' ? d : 'Payment verification failed');
        } finally {
          setPaying(false);
        }
      },
      modal: {
        ondismiss: () => setPaying(false),
      },
    });
    rzp.open();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="upgrade-pro-modal"
        className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white">
              <Crown className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>Upgrade to Pro</DialogTitle>
              <DialogDescription>
                Unlock the full RupiWise toolkit — compliance, hardware,
                branding, and 50 team seats with one one-time payment.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3" data-testid="upgrade-feature-grid">
          {PRO_FEATURE_GROUPS.map((group) => (
            <div
              key={group.title}
              className="rounded-lg border border-slate-200 bg-slate-50/60 p-3"
              data-testid={`upgrade-feature-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <h4 className="text-xs uppercase tracking-wider font-semibold text-amber-700 mb-2">
                {group.title}
              </h4>
              <ul className="space-y-1.5">
                {group.items.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-slate-700 leading-snug">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Tabs defaultValue="razorpay" className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="razorpay" data-testid="upgrade-tab-razorpay">
              <CreditCard className="w-4 h-4 mr-1.5" /> Pay in-app
            </TabsTrigger>
            <TabsTrigger value="code" data-testid="upgrade-tab-code">
              <Key className="w-4 h-4 mr-1.5" /> License code
            </TabsTrigger>
          </TabsList>

          <TabsContent value="razorpay" className="mt-4">
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    One-time payment
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    ₹{(rzpInfo?.amount_inr ?? 1999).toLocaleString('en-IN')}
                  </div>
                </div>
                <Sparkles className="w-8 h-8 text-amber-500" />
              </div>
              {!rzpInfo?.enabled ? (
                <p
                  data-testid="upgrade-razorpay-disabled"
                  className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2"
                >
                  In-app payments are not yet configured. Use the License code tab,
                  or contact support to purchase via Amazon / Flipkart / offline.
                </p>
              ) : (
                <Button
                  onClick={handleRazorpayPay}
                  disabled={paying}
                  data-testid="upgrade-razorpay-pay"
                  className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                >
                  {paying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Razorpay…
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" /> Pay with Razorpay
                    </>
                  )}
                </Button>
              )}
              <p className="text-[10px] text-slate-500 text-center mt-2">
                Secure payment powered by Razorpay · UPI / Cards / Netbanking
              </p>
            </div>
          </TabsContent>

          <TabsContent value="code" className="mt-4">
            <form onSubmit={handleCodeUpgrade} className="space-y-3" data-testid="upgrade-code-form">
              <p className="text-sm text-slate-600">
                Already purchased a Pro license from Amazon, Flipkart or offline?
                Paste the code below.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="upg-code">Pro license code</Label>
                <Input
                  id="upg-code"
                  value={licenseCode}
                  onChange={(e) => setLicenseCode(e.target.value.toUpperCase())}
                  placeholder="PRO-XXXX-XXXX-XXXX"
                  className="font-mono tracking-wider"
                  data-testid="upgrade-code-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submittingCode}
                data-testid="upgrade-code-submit"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              >
                {submittingCode ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" /> Apply license &amp; upgrade
                  </>
                )}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
